import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getPrograms } from "../../app/api";
import { Link } from "react-router-dom";
import { Pagination } from "antd";
import FloatingShapes from "../common/FloatingShapes";

const stripHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return doc.body.textContent || '';
};

export const PROGRAM_CATEGORIES = [
  'Бүлгэмийн ажил',
  'Бүлэг дундын ажил',
  'Бүлгийн ажил',
];

export const ProgramList = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const pageSize = 12;
  const [Data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const q: any = {
      offset: ((page - 1) * pageSize).toString(),
      limit: (pageSize).toString(),
    };
    if (category) q.category = category;

    getPrograms(new URLSearchParams(q)).then((res: any) => {
      if (cancelled) return;
      // Шинэ API нь {results, total}, хуучин нь bare array буцаадаг.
      // Server deploy болохоос өмнө ч хуудас ажиллахын тулд хоёуланг нь дэмжинэ.
      if (res && res.data && Array.isArray(res.data.results)) {
        setData(res.data.results);
        setTotalCount(res.data.total);
      } else if (res && Array.isArray(res.data)) {
        setData(res.data);
        setTotalCount(res.data.length);
      } else {
        setData([]);
        setTotalCount(0);
      }
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setData([]);
      setTotalCount(0);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [page, category]);

  const selectCategory = (value: string | undefined) => {
    setCategory(value);
    setPage(1);
  };

  return (
    <>
      {loading && (
        <div className="w-full h-full fixed top-0 left-0 bg-white/95 z-50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-800 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Dark hero header */}
      <div className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-accent-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative pt-40 pb-16 sm:pt-48 sm:pb-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-secondary-300 backdrop-blur-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
              {t('programs.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight">
              <span className="bg-gradient-to-r from-secondary-400 to-accent-400 bg-clip-text text-transparent">{t('programs.title')}</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              {t('programs.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-neutral-50 py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

          {/* Category filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <button
              type="button"
              onClick={() => selectCategory(undefined)}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-200 ${!category
                ? 'bg-primary-600 text-white shadow-lg'
                : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'}`}
            >
              {t('programs.all')}
            </button>
            {PROGRAM_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => selectCategory(c)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-200 ${category === c
                  ? 'bg-primary-600 text-white shadow-lg'
                  : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Grid */}
          {Data.length === 0 && !loading ? (
            <div className="py-20 text-center text-neutral-500">
              {t('programs.noPrograms')}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {Data.map((program: any, index: number) => (
                <Link
                  key={program.id}
                  to={`/programs/${program.id}`}
                  className="group block bg-white rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 overflow-hidden animate-slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
                    {program.thumb && (
                      <img
                        src={program.thumb}
                        alt={program.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-out"
                      />
                    )}
                    {program.category && (
                      <span className="absolute top-3 left-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-white/90 text-primary-700 backdrop-blur-sm">
                        {program.category}
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    {program.theme && (
                      <span className="text-xs font-semibold text-secondary-600 uppercase tracking-wide">
                        {program.theme}
                      </span>
                    )}
                    <h3 className="mt-1 text-xl font-extrabold text-neutral-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {program.title}
                    </h3>
                    <p className="mt-2 text-neutral-500 leading-relaxed line-clamp-3 text-[15px]">
                      {stripHtml(program.description)}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-400">
                      {program.durationMinutes ? (
                        <span>{program.durationMinutes} {t('programs.minutes')}</span>
                      ) : null}
                      {program.ageGroups && program.ageGroups.length > 0 && (
                        <span>{program.ageGroups.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalCount != null && totalCount > pageSize && (
            <div className="flex justify-center mt-12">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={totalCount}
                showSizeChanger={false}
                onChange={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="[&_.ant-pagination-item]:rounded-full [&_.ant-pagination-item]:border-neutral-200 [&_.ant-pagination-item-active]:bg-primary-600 [&_.ant-pagination-item-active]:border-primary-600 [&_.ant-pagination-item-active_a]:text-white [&_.ant-pagination-item:hover]:border-neutral-400"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProgramList;
